/**
 * Is a newer build live than the one this page is running?
 *
 * A page left open across a deploy keeps the old code until it is reloaded,
 * so a fix can be live and still not reach somebody looking right at it. This
 * asks /version.json (written by vite.config.ts at build time, served no-cache)
 * every few minutes and whenever the tab comes back into view, and reports
 * when it names a different build.
 *
 * It also listens for Vite's preload error: a page whose lazily-loaded chunk
 * was removed by a deploy cannot open that screen until it reloads, which is
 * the same answer.
 *
 * It never reloads by itself. Somebody may be halfway through a form.
 */
import { useEffect, useState } from 'react';

export const CHECK_EVERY_MS = 5 * 60 * 1000;

/** Different, and both known. A missing or unreadable file is never "new". */
export function isNewBuild(running: string, live: unknown): boolean {
  const build = (live as { build?: unknown } | null)?.build;
  return typeof build === 'string' && build !== '' && running !== '' && build !== running;
}

export async function fetchLiveBuild(fetcher: typeof fetch = fetch): Promise<unknown> {
  try {
    const res = await fetcher('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null; // offline, or the dev server, which has no such file
  }
}

export function useNewVersion(running: string = __BUILD_ID__): boolean {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (available) return undefined;
    let cancelled = false;
    const check = async () => {
      const live = await fetchLiveBuild();
      if (!cancelled && isNewBuild(running, live)) setAvailable(true);
    };
    const onVisible = () => { if (document.visibilityState === 'visible') void check(); };
    const onPreloadError = () => setAvailable(true);

    const timer = window.setInterval(() => { void check(); }, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener('vite:preloadError', onPreloadError);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener('vite:preloadError', onPreloadError);
    };
  }, [running, available]);

  return available;
}
